import { ContentHistory } from '@fs/zion-icon/dist/cjs/icons'
import {
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Divider,
  Paragraph,
  Row,
  TextField,
  useOverlay,
  DangerButton,
  Button,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { getUserRollbackResults, userRollback } from '../../../services/userService'
import { UserContext } from '../UserContext'

export default function UserRollbackResultsButtonAndDialog() {
  const { t } = useTranslation()
  const { error, info } = useNotifications()
  const { currentUser } = useContext(UserContext)
  const overlay = useOverlay()
  const [contributorIdConfirmation, setContributorIdConfirmation] = useState('')
  const [cutoffTimeStamp, setCutoffTimeStamp] = useState()
  const [canForce, setCanForce] = useState(false)
  const [rollbackAgain, setRollbackAgain] = useState(false)
  const contributorId = currentUser.contributorId
  const disableRollbackBtn = contributorIdConfirmation !== contributorId
  const focusElementRef = useRef()

  const executeRollback = async () => {
    try {
      await userRollback(contributorId, cutoffTimeStamp, rollbackAgain)
      overlay.close()
      info(t('user.rollback.rollback.inProgress', 'Rollback now in progress'))
    } catch (err) {
      error(
        t('user.errors.executingUserRollback', {
          defaultValue: `Error executing user rollback: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  useEffect(() => {
    getUserRollbackResults(currentUser.contributorId)
      .then((response) => {
        if (response.data.percentDone === 100) {
          setCanForce(true)
        } else {
          setCanForce(false)
        }
      })
      .catch(() => {
        setCanForce(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleForceRollback = (e) => {
    setRollbackAgain(e.target.checked)
  }

  useEffect(() => {
    if (!overlay.isOpen && overlay.transitioning) {
      setContributorIdConfirmation('')
      setCutoffTimeStamp(undefined)
    }
  }, [overlay.isOpen, overlay.transitioning])

  return (
    <>
      <DangerButton id="rollbackBtn" onClick={overlay.handleClick}>
        {t('user.rollback.rollback.rollback', 'Rollback')}
      </DangerButton>
      <DialogOverlay
        focusRef={focusElementRef}
        id="rollbackOverlay"
        {...overlay}
        title={t('user.rollback.rollback.warning', 'Warning').toUpperCase()}
      >
        <Paragraph size="sm">
          {t(
            'user.rollback.rollback.warningMessage',
            'This call is not easy to undo. It will undo up to 3,000 changes the contributor has made. Use with EXTREME caution. Nothing older than 1 year will be undone.'
          )}
        </Paragraph>
        <Row>
          <Divider />
        </Row>
        <Row alignX="center">
          <Paragraph size="sm">
            {t(
              'user.rollback.rollback.timestamp',
              '(Optional) Paste timestamp to remove changes from that moment through today.'
            )}
          </Paragraph>
        </Row>
        <Row alignX="center" guttersY="none">
          <TextField
            id="cutOffTimeStamp"
            value={cutoffTimeStamp}
            placeholder="01 January 2000 01:01:01"
            onChange={(e) => setCutoffTimeStamp(e.target.value)}
            type="text"
          />
        </Row>
        <Row alignX="center">
          <Paragraph size="sm">
            {t('user.rollback.rollback.confirmation', 'Please type { contributorId } to confirm.', {
              contributorId,
            })}
          </Paragraph>
        </Row>
        <Row alignX="center" guttersY="none">
          <TextField
            ref={focusElementRef}
            id="contribText"
            value={contributorIdConfirmation}
            onChange={(e) => setContributorIdConfirmation(e.target.value)}
            type="text"
          />
        </Row>
        {canForce && (
          <Row alignX="center" guttersY="none">
            <Checkbox
              name="forceNewRollback"
              label={t('user.rollback.rollback.new', 'Force New Rollback')}
              checked={rollbackAgain}
              onChange={handleForceRollback}
            />
          </Row>
        )}
        <Row alignX="center" guttersY="none">
          <DangerButton
            id="executeRollbackBtn"
            Icon={ContentHistory}
            onClick={executeRollback}
            disabled={disableRollbackBtn}
            alignY="nextToInput"
          >
            {t('user.rollback.rollback.executeRollback', 'Execute Rollback')}
          </DangerButton>
        </Row>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={overlay.close}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}
